import { useState } from 'react'
import { Alert, Group } from '@mantine/core'
import { AppButton, AppText } from '../../app/common/ui'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useFeatureFlag } from '../../app/hooks/useFeatureFlag'

type Props = {
  buildId: string
  inviteId: string
  role?: string | null
  buildName: string
  onResponded: () => void
}

export default function BuildPendingInviteBanner ({
  buildId,
  inviteId,
  role,
  buildName,
  onResponded
}: Props) {
  const privacyOn = useFeatureFlag('builds-privacy-collaboration-enabled')
  const [busy, setBusy] = useState(false)

  if (!privacyOn) return null

  const roleLabel = role === 'Viewer' ? 'a viewer' : 'an editor'

  const accept = async () => {
    setBusy(true)
    try {
      await agent.Builds.acceptCollaborator(buildId, inviteId)
      toast.success('Invite accepted — you can collaborate on this build.')
      onResponded()
    } catch {
      /* interceptor */
    } finally {
      setBusy(false)
    }
  }

  const decline = async () => {
    setBusy(true)
    try {
      await agent.Builds.removeCollaborator(buildId, inviteId)
      toast.info('Invite declined.')
      onResponded()
    } catch {
      /* interceptor */
    } finally {
      setBusy(false)
    }
  }

  return (
    <Alert
      mb="md"
      variant="outline"
      color="yellow"
      title="Collaboration invite"
      sx={{ borderColor: 'rgba(210, 153, 34, 0.45)', background: 'rgba(210, 153, 34, 0.08)' }}
    >
      <AppText size="sm" mb="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)' }}>
        You were invited to collaborate on &quot;{buildName}&quot; as {roleLabel}.
      </AppText>
      <Group spacing="sm">
        <AppButton size="xs" onClick={accept} loading={busy} sx={{ background: 'var(--gs-accent)' }}>
          Accept
        </AppButton>
        <AppButton size="xs" variant="default" onClick={decline} disabled={busy}>
          Decline
        </AppButton>
      </Group>
    </Alert>
  )
}
